import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import cx from 'classnames';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import TextField from 'components/form/TextInput';
import {
  AlertHexagonFilledIcon,
  AlertTriangleFilledIcon,
  CheckCircleFilledIcon,
  InfoCircleFilledIcon,
} from 'components/icons';
import React, { useState } from 'react';

import { makeStyles } from '@mui/styles';
import { TYPES } from 'context/alert';

const useStyles = makeStyles(() => ({
  paper: {
    overflow: 'visible',
    p: 3,
  },
  alignCenter: {
    textAlign: 'center',
    justifyContent: 'center',
  },
}));

const SeverityIcon = ({ severity, icon }) => {
  const IconMap = {
    error: AlertHexagonFilledIcon,
    warning: AlertTriangleFilledIcon,
    info: InfoCircleFilledIcon,
    success: CheckCircleFilledIcon,
  };
  const theme = useTheme();

  const Icon = icon || IconMap[severity] || InfoCircleFilledIcon;
  return (
    <Box
      sx={{
        borderRadius: '50%',
        width: 120,
        height: 120,
        bgcolor: alpha(theme.palette[severity].main, 0.2),
        mb: 3,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: severity + '.main',
        }}
      >
        <Icon sx={{ fontSize: 48, color: severity + '.subtle' }} />
      </Box>
    </Box>
  );
};

const AlertComponent = ({ options, close }) => {
  const [val, setVal] = useState(options.inputValue);
  const classes = useStyles();
  const isPrompt = options.type === TYPES.PROMPT;
  const isConfirm = options.type === TYPES.CONFIRM;

  const onOK = (customValue) => {
    if (options.onConfirm) {
      const result = options.onConfirm(isPrompt ? val : customValue);
      if (result && result.then) {
        return result.then(close);
      } else {
        close();
      }
    } else {
      close();
    }
  };

  const onCancel = () => {
    if (options.onCancel) {
      options.onCancel();
    }
    close();
  };

  let actions;
  if (options.actions) {
    if (options.actions.length) {
      actions = options.actions.map((action, index) => {
        const { label, onClick, ...rest } = action;
        const handler = onClick ? (event) => onClick(event, close) : onOK;
        return (
          <Button key={index} onClick={handler} {...rest}>
            {action.label}
          </Button>
        );
      });
    }
  } else {
    actions = (
      <>
        {options.showCancelButton !== false && !isPrompt && (
          <Button
            color="secondary"
            onClick={onCancel}
            variant="outlined"
            sx={{ mr: 'auto' }}
          >
            {options.cancellationText}
          </Button>
        )}
        <Button
          color={options.severity === 'error' ? 'error' : 'primary'}
          onClick={onOK}
          disabled={isPrompt && !options.isValidInput(val)}
        >
          {options.confirmationText}
        </Button>
      </>
    );
  }

  const dialogContent = options.render ? (
    options.render({
      onConfirm: onOK,
      onCancel,
    })
  ) : (
    <>
      {isPrompt ? (
        options.renderInput ? (
          options.renderInput(val, setVal)
        ) : (
          <TextField
            fullWidth
            autoFocus
            margin="none"
            label={options.label}
            multiline={options.multiline}
            rows={options.rows}
            type={options.inputType || 'text'}
            value={val}
            placeholder={options.placeholder}
            onChange={(e) => setVal(e.target.value)}
          />
        )
      ) : null}
    </>
  );

  if (isPrompt || !isConfirm) {
    return (
      <Modal
        maxWidth={options.dialogSize || 'sm'}
        scroll="body"
        title={options.title}
        open={true}
        onClose={options.closable === false ? null : onCancel}
        disableContentWrapper={!!options.render}
        actions={actions}
      >
        {options.description && (
          <DialogContentText variant="subtitle1">
            {options.description}
          </DialogContentText>
        )}
        {dialogContent}
      </Modal>
    );
  }

  if (options.type === TYPES.CONFIRM) {
    return (
      <Dialog
        open
        fullWidth
        maxWidth={options.dialogSize || 'sm'}
        scroll="body"
        className={options.dialogClassName}
        classes={{
          paper: cx(classes.paper),
          [classes.alignCenter]: options.type === TYPES.CONFIRM,
        }}
      >
        <DialogContent
          dividers={false}
          sx={{ p: 5 }}
          style={options.dialogStyle}
          className={cx(classes.paper, {
            [classes.alignCenter]: options.type === TYPES.CONFIRM,
          })}
        >
          {options.severity && (
            <SeverityIcon severity={options.severity} icon={options.icon} />
          )}
          {options.title && (
            <Typography gutterBottom variant="h4">
              {options.title}
            </Typography>
          )}
          {options.description && (
            <DialogContentText color="textSecondary" variant="subtitle1">
              {options.description}
            </DialogContentText>
          )}
          {dialogContent}
          {actions && (
            <Stack spacing={2} direction="row" justifyContent="center" mt={3}>
              {actions}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    );
  }
};

export default AlertComponent;
